import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete, Button, Card, CardContent, Divider, IconButton, Pagination, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { CadastroModelo } from '../../../interface/ICadastroModelo';
import { ModeloAgendamento } from '../../../interface/IAgendamento';

type ModeloAssociado = {
  idModeloCadastro: string;
  minimo: number;
  maximo: number | null;
  campo: string;
  rotulo: string;
  ordem: number;
};

type CriarModeloAgendamentoProps = {
  modelos: CadastroModelo[];
  onBack: () => void;
  onSave: (agendamento: Omit<ModeloAgendamento, 'empresa'>) => Promise<void>;
  saving: boolean;
  agendamento?: ModeloAgendamento;
};

export default function CriarModeloAgendamento({ modelos, onBack, onSave, saving, agendamento }: CriarModeloAgendamentoProps) {
  const [nome, setNome] = useState(agendamento?.nome || '');
  const [modelosAssociados, setModelosAssociados] = useState<ModeloAssociado[]>(agendamento?.regrasCadastro.map((regra, indice) => {
    const resumo = agendamento.camposResumo.find((campoResumo) => campoResumo.idRegraCadastro === regra.id);
    return { idModeloCadastro: regra.idModeloCadastro, minimo: regra.minimo, maximo: regra.maximo, campo: resumo?.campo || '', rotulo: resumo?.rotulo || '', ordem: resumo?.ordem || indice + 1 };
  }) || []);
  const [salvando, setSalvando] = useState(false);
  const [paginaAssociacoes, setPaginaAssociacoes] = useState(1);

  const adicionarModelo = () => {
    setModelosAssociados((anteriores) => [...anteriores, { idModeloCadastro: '', minimo: 1, maximo: null, campo: '', rotulo: '', ordem: anteriores.length + 1 }]);
  };

  const atualizarModelo = (indice: number, alteracao: Partial<ModeloAssociado>) => {
    setModelosAssociados((anteriores) => anteriores.map((modelo, index) => index === indice ? { ...modelo, ...alteracao } : modelo));
  };

  const removerModelo = (indice: number) => {
    setModelosAssociados((anteriores) => anteriores.filter((_, index) => index !== indice));
  };

  const selecionarModelo = (indice: number, idModeloCadastro: string) => {
    if (idModeloCadastro && modelosAssociados.some((modelo, index) => index !== indice && modelo.idModeloCadastro === idModeloCadastro)) return;
    atualizarModelo(indice, { idModeloCadastro, campo: '', rotulo: '' });
  };

  const selecionarCampoResumo = (indice: number, campo: string) => {
    setModelosAssociados((anteriores) => {
      const atualizados = anteriores.map((modelo, index) => index === indice ? { ...modelo, campo } : modelo);
      const totalCamposSelecionados = Math.max(1, atualizados.filter((modelo) => modelo.campo).length);
      const ordensUsadas = new Set<number>();
      return atualizados.map((modelo) => {
        if (!modelo.campo) return modelo;
        const ordemAtual = Math.min(Math.max(1, modelo.ordem), totalCamposSelecionados);
        const ordem = !ordensUsadas.has(ordemAtual)
          ? ordemAtual
          : Array.from({ length: totalCamposSelecionados }, (_, index) => index + 1).find((valor) => !ordensUsadas.has(valor)) || 1;
        ordensUsadas.add(ordem);
        return { ...modelo, ordem };
      });
    });
  };

  const alterarOrdem = (indice: number, ordem: number) => {
    setModelosAssociados((anteriores) => {
      const totalCamposSelecionados = Math.max(1, anteriores.filter((modelo) => modelo.campo).length);
      const novaOrdem = Math.min(totalCamposSelecionados, Math.max(1, ordem));
      const ordemAnterior = anteriores[indice].ordem;
      const indiceOrdemOcupada = anteriores.findIndex((modelo, index) => index !== indice && modelo.campo && modelo.ordem === novaOrdem);
      return anteriores.map((modelo, index) => {
        if (index === indice) return { ...modelo, ordem: novaOrdem };
        if (index === indiceOrdemOcupada) return { ...modelo, ordem: ordemAnterior };
        return modelo;
      });
    });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await onSave({
      nome,
      regrasCadastro: modelosAssociados.map((modelo, indice) => ({
        id: `${modelo.idModeloCadastro}_${indice}`,
        nome: modelos.find((item) => item.id === modelo.idModeloCadastro)?.nome || '',
        idModeloCadastro: modelo.idModeloCadastro,
        minimo: modelo.minimo,
        maximo: modelo.maximo,
      })),
      camposResumo: modelosAssociados.reduce<ModeloAgendamento['camposResumo']>((camposResumo, modelo, indice) => {
        if (modelo.campo) camposResumo.push({
          idRegraCadastro: `${modelo.idModeloCadastro}_${indice}`,
          campo: modelo.campo,
          rotulo: modelo.rotulo,
          ordem: modelo.ordem,
        });
        return camposResumo;
      }, []),
      });
    } catch {
      // A mensagem de erro já é apresentada pela mutation.
    } finally {
      setSalvando(false);
    }
  };

  const totalCamposSelecionados = Math.max(1, modelosAssociados.filter((modelo) => modelo.campo).length);
  const possuiModeloDisponivel = modelos.some((modelo) => !modelosAssociados.some((associado) => associado.idModeloCadastro === modelo.id));
  const bloqueado = saving || salvando;
  const totalPaginasAssociacoes = Math.max(1, Math.ceil(modelosAssociados.length / 5));

  useEffect(() => {
    if (paginaAssociacoes > totalPaginasAssociacoes) setPaginaAssociacoes(totalPaginasAssociacoes);
  }, [paginaAssociacoes, totalPaginasAssociacoes]);

  return (
    <Stack direction='column' alignItems='flex-start' width='100%' paddingX='10%' spacing={3}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' width='100%'>
        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton size='large' onClick={onBack} aria-label='Voltar para modelos de agendamento'>
            <ArrowBackIcon sx={{ color: '#000000' }} />
          </IconButton>
          <Typography variant='h6' fontWeight={700}>{agendamento ? 'Editar modelo de Agendamento' : 'Novo modelo de Agendamento'}</Typography>
        </Stack>
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={salvar}
          loading={bloqueado}
          disabled={bloqueado || !nome.trim() || !modelosAssociados.length || modelosAssociados.some((modelo, indice) => !modelo.idModeloCadastro || modelo.minimo < 1 || (modelo.maximo !== null && modelo.minimo > modelo.maximo) || (modelo.campo && (modelo.ordem < 1 || modelo.ordem > totalCamposSelecionados || modelosAssociados.some((outroModelo, outroIndice) => outroIndice !== indice && outroModelo.campo && outroModelo.ordem === modelo.ordem))))}
          startIcon={<SaveIcon />}
        >
          Salvar
        </Button>
      </Stack>

      <TextField
        label='Nome do modelo de agendamento'
        value={nome}
        onChange={(event) => setNome(event.target.value)}
        disabled={bloqueado}
        fullWidth
        sx={{ bgcolor: 'grey.50' }}
      />
      {modelosAssociados.length > 0 && <Card variant='outlined' sx={{ width: '100%', bgcolor: 'grey.50' }}>
        <CardContent>
          <Stack direction='column' width='100%' spacing={2}>
            {modelosAssociados.map((modeloAssociado, indice) => {
          const modeloSelecionado = modelos.find((modelo) => modelo.id === modeloAssociado.idModeloCadastro);
          return (
            <Stack key={indice} direction='column' width='100%' spacing={2}>
              {indice > 0 && <Divider flexItem />}
              <Stack direction='row' width='100%' spacing={2} alignItems='center'>
                <Autocomplete
                  fullWidth
                  options={modelos.filter((modelo) => modelo.id === modeloAssociado.idModeloCadastro || !modelosAssociados.some((associado) => associado.idModeloCadastro === modelo.id))}
                  value={modeloSelecionado || null}
                  getOptionLabel={(modelo) => modelo.nome || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, modelo) => selecionarModelo(indice, modelo?.id || '')}
                  disabled={bloqueado}
                  renderInput={(params) => <TextField {...params} label='Modelo' sx={{ bgcolor: 'grey.50' }} />}
                />
                <TextField label='Min' type='number' value={modeloAssociado.minimo} onChange={(event) => atualizarModelo(indice, { minimo: Math.max(1, Number(event.target.value)) })} disabled={bloqueado} inputProps={{ min: 1 }} sx={{ width: 120, bgcolor: 'grey.50' }} />
                <Tooltip title='Deixe vazio para ilimitado'>
                  <TextField label='Max' type='number' value={modeloAssociado.maximo ?? ''} onChange={(event) => atualizarModelo(indice, { maximo: event.target.value === '' ? null : Math.max(1, Number(event.target.value)) })} disabled={bloqueado} inputProps={{ min: 1 }} error={modeloAssociado.maximo !== null && modeloAssociado.minimo > modeloAssociado.maximo} helperText={modeloAssociado.maximo !== null && modeloAssociado.minimo > modeloAssociado.maximo ? 'Máximo deve ser maior ou igual ao mínimo' : undefined} sx={{ width: 120, bgcolor: 'grey.50' }} />
                </Tooltip>
                <IconButton color='error' onClick={() => removerModelo(indice)} disabled={bloqueado} aria-label='Remover modelo'>
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </Stack>
              {modeloSelecionado && (
                <Stack direction='row' width='100%' spacing={2}>
                  <Autocomplete
                    fullWidth
                    options={modeloSelecionado.campos || []}
                    value={modeloSelecionado.campos?.find((campo) => campo.nome === modeloAssociado.campo) || null}
                    getOptionLabel={(campo) => campo.nome || ''}
                    isOptionEqualToValue={(option, value) => option.nome === value.nome}
                    onChange={(_, campo) => selecionarCampoResumo(indice, campo?.nome || '')}
                    disabled={bloqueado}
                    renderInput={(params) => <TextField {...params} label='Campo para resumo' sx={{ bgcolor: 'grey.50' }} />}
                  />
                  <TextField label='Rótulo' value={modeloAssociado.rotulo} onChange={(event) => atualizarModelo(indice, { rotulo: event.target.value })} disabled={bloqueado} sx={{ bgcolor: 'grey.50' }} />
                  <TextField label='Ordem' type='number' value={modeloAssociado.ordem} onChange={(event) => alterarOrdem(indice, Number(event.target.value))} disabled={bloqueado} inputProps={{ min: 1, max: totalCamposSelecionados }} error={Boolean(modeloAssociado.campo && modelosAssociados.some((outroModelo, outroIndice) => outroIndice !== indice && outroModelo.campo && outroModelo.ordem === modeloAssociado.ordem))} helperText={modeloAssociado.campo && modelosAssociados.some((outroModelo, outroIndice) => outroIndice !== indice && outroModelo.campo && outroModelo.ordem === modeloAssociado.ordem) ? 'Esta ordem já está em uso' : undefined} sx={{ width: 120, bgcolor: 'grey.50' }} />
                </Stack>
              )}
            </Stack>
          );
            }).slice((paginaAssociacoes - 1) * 5, paginaAssociacoes * 5)}
          </Stack>
          {totalPaginasAssociacoes > 1 && <Pagination count={totalPaginasAssociacoes} page={paginaAssociacoes} onChange={(_, novaPagina) => setPaginaAssociacoes(novaPagina)} color='primary' sx={{ display: 'flex', justifyContent: 'center', mt: 3 }} />}
        </CardContent>
      </Card>}

      <Button variant='text' size='large' onClick={adicionarModelo} disabled={bloqueado || !possuiModeloDisponivel} startIcon={<AddCircleOutlineOutlinedIcon />}>
        Adicionar modelo
      </Button>
    </Stack>
  );
}
